<template>
    <!-- the customer card's tab row: a gray strip along the top of the card, the tabs split by the
         same thin line as the blocks of the summary strip above it, and the open tab a white tab in
         front of the others (purple top edge, a small shadow) that runs straight into the content
         under it - no line and no gap between them.
         Short of room, a label of two words or more breaks into two lines (never three); still short,
         the strip scrolls sideways, with an arrow at each end that has more tabs behind it - it never
         spills out of the card.
         The border-tab / nav-tabs / nav-link classes stay on purpose: the e2e tests find the tabs by them.
         The settings card (Settings.vue) has the same tab row, and so do the money pages (/expected-payments,
         /checks) - they pass { id, label } tabs instead of translation keys - and /tasks, whose tabs carry a
         count too ({ id, label, count }: the number in a pill after the label). -->
    <div class="customerTabsBar">
        <div ref="strip"
             class="border-tab nav nav-tabs customerTabs"
             @scroll="updateArrows">
            <li v-for="(item, i) in items"
                :key="item.tab"
                class="nav-item"
                :class="{ customerTabOpen: item.tab == activeTab, customerTabBeforeOpen: i + 1 < items.length && items[i + 1].tab == activeTab }">
                <a class="nav-link"
                   :class="{ active: item.tab == activeTab }"
                   @click="$emit('select', item.tab)">
                    <!-- one line per span; the space between them is where a long label breaks -->
                    <span class="customerTabLabel"><span class="customerTabLine" :data-text="item.lines[0]">{{ item.lines[0] }}</span> <span v-if="item.lines[1]" class="customerTabLine" :data-text="item.lines[1]">{{ item.lines[1] }}</span></span>
                    <span v-if="item.count !== null"
                          class="customerTabCount">{{ item.count }}</span>
                </a>
            </li>
        </div>

        <!-- RTL: the start of the strip is its right end -->
        <button v-show="canScrollStart"
                type="button"
                class="customerTabsArrow customerTabsArrowStart"
                aria-label="טאבים קודמים"
                @click="scrollStripBy(stepSize())">
            <span><svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6" /></svg></span>
        </button>
        <button v-show="canScrollEnd"
                type="button"
                class="customerTabsArrow customerTabsArrowEnd"
                aria-label="טאבים נוספים"
                @click="scrollStripBy(-stepSize())">
            <span><svg viewBox="0 0 24 24"><path d="m15 18-6-6 6-6" /></svg></span>
        </button>
    </div>
</template>

<script>
// the label on two lines when its tab is short of room: one break, between words, where the
// longer line comes out shortest - and never right before a "/" (אבני דרך / | משימות)
function splitLabel(label) {
    let words = String(label).split(' ').filter(word => word);
    let lines = [String(label)];
    let longest = Infinity;

    for (let i = 1; i < words.length; i++) {
        if (words[i] == '/')
            continue;

        let first = words.slice(0, i).join(' ');
        let second = words.slice(i).join(' ');

        if (Math.max(first.length, second.length) < longest) {
            lines = [first, second];
            longest = Math.max(first.length, second.length);
        }
    }

    return lines;
}

// room kept clear of the arrow when a tab is scrolled into view
const ARROW_ROOM = 44;

export default {
    name: 'CustomerTabsBar',

    props: {
        tabs: { type: Array, default: () => [] },
        activeTab: { type: String, default: null }
    },

    data() {
        return {
            canScrollStart: false,
            canScrollEnd: false
        };
    },

    computed: {
        // a tab is a translation key, or { id, label } for a page that names its own tabs - and
        // { id, label, count } for a tab with a number beside its label (the tasks page)
        items() {
            return this.tabs.map(tab => tab && typeof tab == 'object'
                ? { tab: tab.id, lines: splitLabel(tab.label), count: typeof tab.count == 'number' ? tab.count : null }
                : { tab: tab, lines: splitLabel(this.$t(tab)), count: null });
        }
    },

    watch: {
        activeTab() {
            this.$nextTick(this.showOpenTab);
        },

        tabs() {
            this.$nextTick(this.onResize);
        }
    },

    mounted() {
        // the strip narrows with the window and with the side menu opening / closing
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(this.onResize);
            this.resizeObserver.observe(this.$refs.strip);
        }
        else {
            window.addEventListener('resize', this.onResize);
        }

        this.$nextTick(this.onResize);
    },

    beforeDestroy() {
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
        else
            window.removeEventListener('resize', this.onResize);
    },

    methods: {
        onResize() {
            this.updateArrows();
            this.showOpenTab();
        },

        updateArrows() {
            let strip = this.$refs.strip;

            if (!strip)
                return;

            // RTL: scrollLeft is 0 at the start (the right end) and goes negative towards the end
            let hidden = strip.scrollWidth - strip.clientWidth;
            let fromStart = Math.abs(strip.scrollLeft);

            this.canScrollStart = hidden > 1 && fromStart > 1;
            this.canScrollEnd = hidden > 1 && fromStart < hidden - 1;
        },

        stepSize() {
            return Math.max(120, Math.round(this.$refs.strip.clientWidth * 0.6));
        },

        // + shows more of the start (right), - more of the end (left)
        scrollStripBy(delta) {
            let strip = this.$refs.strip;
            let target = strip.scrollLeft + delta;
            let request = this.scrollRequest = (this.scrollRequest || 0) + 1;

            strip.scrollBy({ left: delta, behavior: 'smooth' });

            // a tab in the background runs a smooth scroll partly or not at all - land on the
            // target anyway (the browser clamps it to the end of the strip), unless a newer
            // scroll (a second click on the arrow) took over
            setTimeout(() => {
                if (request == this.scrollRequest)
                    strip.scrollLeft = target;
            }, 800);
        },

        // the open tab in full view: a tab clicked under an arrow, or opened by a link (#seating)
        // while it is scrolled out of sight
        showOpenTab() {
            let strip = this.$refs.strip;
            let open = strip && strip.querySelector('.customerTabOpen');

            if (!open || strip.scrollWidth <= strip.clientWidth)
                return;

            let stripBox = strip.getBoundingClientRect();
            let openBox = open.getBoundingClientRect();

            // only as far as the strip can still go that way: an open tab at either end is in view
            // already (no arrow there), and a scroll that cannot move must not start
            let toStart = Math.abs(strip.scrollLeft);
            let toEnd = strip.scrollWidth - strip.clientWidth - toStart;

            if (openBox.right > stripBox.right - ARROW_ROOM && toStart > 1)
                this.scrollStripBy(Math.min(toStart, openBox.right - (stripBox.right - ARROW_ROOM)));
            else if (openBox.left < stripBox.left + ARROW_ROOM && toEnd > 1)
                this.scrollStripBy(-Math.min(toEnd, (stripBox.left + ARROW_ROOM) - openBox.left));
        }
    }
};
</script>

<style>
/* the strip is the top of the card: the card's corners, a shade darker than the card */
.customerTabsBar {
    position: relative;
    background: #f4f5f9;
    border-radius: 6px 6px 0 0;
}

/* !important where public/index.html (under 992px) and the theme force their own tab look */
.customerTabsBar .customerTabs.border-tab.nav-tabs {
    display: flex !important;
    flex-wrap: nowrap !important;
    align-items: stretch;
    margin: 0 !important;
    padding: 0;
    border: 0 !important;
    border-radius: inherit;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    white-space: normal !important;
    /* the line under the tabs: painted under them, so the open (white) tab covers it and runs
       into the content */
    box-shadow: inset 0 -1px 0 #dee2e6;
    scrollbar-width: none;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs::-webkit-scrollbar {
    display: none;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item {
    position: relative;
    display: flex !important;
    flex: 1 1 auto;
    max-width: 240px;
    width: auto !important;
    margin: 0;
    border: 0 !important;
    float: none;
}

/* the separator: the summary strip's thin line, between two tabs where neither is the open one */
.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item::after {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 1px;
    background: #dee2e6;
    pointer-events: none;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item:last-child::after,
.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item.customerTabOpen::after,
.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item.customerTabBeforeOpen::after {
    display: none;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item .nav-link {
    position: relative;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 !important;
    padding: 11px 18px 14px !important;
    border: 0 !important;
    border-top: 3px solid transparent !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: #2a3142 !important;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    text-align: center;
    cursor: pointer;
    transition: color .15s ease, background-color .15s ease;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item .nav-link:hover {
    background: rgba(255, 255, 255, .65) !important;
    color: #7650c4 !important;
}

/* the open tab: in front of the others and one piece with the content below it */
.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item.customerTabOpen {
    z-index: 1;
}

.customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item .nav-link.active {
    background: #fff !important;
    color: #7650c4 !important;
    border-top-color: #ab8ce4 !important;
    border-radius: 8px 8px 0 0 !important;
    box-shadow: 0 0 10px rgba(42, 49, 66, .16);
    cursor: default;
}

.customerTabsBar .customerTabLabel {
    display: block;
}

/* each line keeps the width of its bold self, so opening a tab does not shift the others */
.customerTabsBar .customerTabLine {
    display: inline-block;
    vertical-align: top;
    white-space: nowrap;
}

.customerTabsBar .customerTabLine::after {
    content: attr(data-text);
    display: block;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    font-weight: 600;
}

.customerTabsBar .nav-link.active .customerTabLine {
    font-weight: 600;
}

/* a tab's number (the tasks page): a small pill after the label, the same weight open or not so
   opening a tab does not shift the others */
.customerTabsBar .customerTabCount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    min-width: 24px;
    height: 22px;
    margin-inline-start: 8px;
    padding: 0 7px;
    border-radius: 11px;
    background: #e6e8ef;
    color: #4f546d;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.customerTabsBar .nav-link.active .customerTabCount {
    background: #efe9fb;
    color: #7650c4;
}

/* the arrows over the ends of a strip that scrolls: the tabs behind them fade out */
.customerTabsBar .customerTabsArrow {
    position: absolute;
    top: 0;
    bottom: 1px;
    z-index: 2;
    width: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    color: #6b7185;
    cursor: pointer;
}

.customerTabsBar .customerTabsArrowStart {
    right: 0;
    border-top-right-radius: 6px;
    background: linear-gradient(to left, #f4f5f9 55%, rgba(244, 245, 249, 0));
}

.customerTabsBar .customerTabsArrowEnd {
    left: 0;
    border-top-left-radius: 6px;
    background: linear-gradient(to right, #f4f5f9 55%, rgba(244, 245, 249, 0));
}

.customerTabsBar .customerTabsArrow span {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(42, 49, 66, .18);
    transition: color .15s ease;
}

.customerTabsBar .customerTabsArrow:hover span {
    color: #7650c4;
}

.customerTabsBar .customerTabsArrow svg {
    width: 16px;
    height: 16px;
    flex: none;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
}

@media (max-width: 575px) {
    .customerTabsBar .customerTabs.border-tab.nav-tabs .nav-item .nav-link {
        padding: 10px 13px 13px !important;
        font-size: 14px;
    }
}
</style>
